import {
  AuthenticationError,
  AuthorizationError,
  type DeltaBaseError,
  EventStoreAlreadyExistsError,
  EventStoreNotFoundError,
  InternalServerError,
  InvalidSubscriptionConfigError,
  RateLimitError,
  SerializationError,
  StorageError,
  StreamNotFoundError,
  SubscriptionNotFoundError,
  TimeoutError,
  ValidationError,
  VersionConflictError,
} from '../errors';

export interface HttpClientConfig {
  baseUrl: string;
  apiKey?: string;
  headers?: Record<string, string>;
  /**
   * Custom fetch function to use for HTTP requests.
   * Useful for service bindings in Cloudflare Workers.
   * When provided, this will be used instead of the global fetch.
   */
  fetch?: typeof fetch;
}

/**
 * Interface for version conflict error response from API
 */
interface VersionConflictErrorResponse extends Record<string, unknown> {
  error: 'Version conflict';
  details: string;
  currentVersion: number;
  expectedVersion: number;
}

/**
 * Interface for validation error response from API
 */
interface ValidationErrorResponse extends Record<string, unknown> {
  error: 'Invalid request body';
  details: Array<{
    code: string;
    message: string;
    path: Array<string | number>;
  }>;
}

/**
 * Interface for general error response from API
 */
interface GeneralErrorResponse extends Record<string, unknown> {
  error: string;
  message: string;
  details?: Record<string, unknown>;
}

export class HttpClient {
  private baseUrl: string;
  private apiKey?: string;
  private customHeaders?: Record<string, string>;
  private customFetch?: typeof fetch;

  constructor(config: HttpClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, ''); // Remove trailing slash
    this.apiKey = config.apiKey;
    this.customHeaders = config.headers;
    this.customFetch = config.fetch;
  }

  /**
   * Perform a GET request
   */
  async get<T>(path: string, params?: Record<string, string>): Promise<T> {
    const url = new URL(`${this.baseUrl}${path}`);

    if (params) {
      for (const [key, value] of Object.entries(params)) {
        url.searchParams.append(key, value);
      }
    }

    const fetchFn = this.customFetch || fetch;
    const response = await fetchFn(url.toString(), {
      method: 'GET',
      headers: this.getHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Perform a POST request
   */
  async post<T>(path: string, body?: Record<string, unknown>): Promise<T> {
    const fetchFn = this.customFetch || fetch;
    const response = await fetchFn(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Perform a PUT request
   */
  async put<T>(path: string, body?: Record<string, unknown>): Promise<T> {
    const fetchFn = this.customFetch || fetch;
    const response = await fetchFn(`${this.baseUrl}${path}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Perform a DELETE request
   */
  async delete<T>(path: string): Promise<T> {
    const fetchFn = this.customFetch || fetch;
    const response = await fetchFn(`${this.baseUrl}${path}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    return this.handleResponse<T>(response);
  }

  /**
   * Get common headers for all requests
   */
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // Add API key if provided
    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }

    // Merge any custom headers (used for internal service auth, but SDK doesn't care)
    if (this.customHeaders) {
      Object.assign(headers, this.customHeaders);
    }

    return headers;
  }

  /**
   * Handle API response and convert to typed errors
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    const body = isJson ? await response.json() : await response.text();

    if (!response.ok) {
      throw this.createTypedError(response.status, body);
    }

    return body as T;
  }

  /**
   * Create a typed error based on status code and response body
   */
  private createTypedError(status: number, body: unknown): DeltaBaseError {
    // Handle JSON error responses
    if (typeof body === 'object' && body !== null) {
      const errorBody = body as Record<string, unknown>;

      // Version conflict error (409)
      if (status === 409 && this.isVersionConflictError(errorBody)) {
        return new VersionConflictError(
          errorBody.details,
          body,
          errorBody.currentVersion,
          errorBody.expectedVersion
        );
      }

      // Validation error (400)
      if (status === 400 && this.isValidationError(errorBody)) {
        return new ValidationError(
          'Request validation failed',
          body,
          errorBody.details
        );
      }

      // General error responses with error type
      if (this.isGeneralError(errorBody)) {
        switch (errorBody.error) {
          case 'EVENT_STORE_NOT_FOUND':
            return new EventStoreNotFoundError(
              errorBody.message,
              body,
              ((errorBody.details as Record<string, unknown>)
                ?.eventStoreId as string) || 'unknown'
            );

          case 'SUBSCRIPTION_NOT_FOUND':
            return new SubscriptionNotFoundError(
              errorBody.message,
              body,
              ((errorBody.details as Record<string, unknown>)
                ?.subscriptionId as string) || 'unknown',
              ((errorBody.details as Record<string, unknown>)
                ?.eventStoreId as string) || 'unknown'
            );

          case 'EVENT_STORE_ALREADY_EXISTS':
            return new EventStoreAlreadyExistsError(
              errorBody.message,
              body,
              ((errorBody.details as Record<string, unknown>)
                ?.name as string) || 'unknown'
            );

          case 'TIMEOUT_ERROR':
            return new TimeoutError(
              errorBody.message,
              body,
              ((errorBody.details as Record<string, unknown>)
                ?.timeoutMs as number) || 0,
              ((errorBody.details as Record<string, unknown>)
                ?.operation as string) || 'unknown'
            );

          case 'STORAGE_ERROR':
            return new StorageError(
              errorBody.message,
              body,
              ((errorBody.details as Record<string, unknown>)?.storageType as
                | 'r2'
                | 'sqlite'
                | 'archive') || 'sqlite',
              ((errorBody.details as Record<string, unknown>)?.operation as
                | 'read'
                | 'write'
                | 'delete') || 'read'
            );

          case 'STREAM_NOT_FOUND':
            return new StreamNotFoundError(
              errorBody.message,
              body,
              ((errorBody.details as Record<string, unknown>)
                ?.streamId as string) || 'unknown',
              ((errorBody.details as Record<string, unknown>)
                ?.eventStoreId as string) || 'unknown'
            );

          case 'SERIALIZATION_ERROR':
            return new SerializationError(
              errorBody.message,
              body,
              ((errorBody.details as Record<string, unknown>)?.dataType as
                | 'event'
                | 'metadata'
                | 'filter'
                | 'webhook_payload') || 'event',
              (errorBody.details as Record<string, unknown>)
                ?.originalData as string
            );

          case 'INVALID_CALLBACK_URL':
          case 'INVALID_EVENT_FILTER':
          case 'INVALID_SUBSCRIPTION_CONFIG':
            return new InvalidSubscriptionConfigError(
              errorBody.message,
              body,
              errorBody.error
            );

          case 'INTERNAL_SERVER_ERROR':
            return new InternalServerError(errorBody.message, body);
        }
      }
    }

    // Handle standard HTTP status codes
    switch (status) {
      case 401:
        return new AuthenticationError('Authentication failed', body);

      case 403:
        return new AuthorizationError('Authorization failed', body);

      case 404:
        // Try to determine if it's a stream or event store not found
        if (typeof body === 'object' && body !== null) {
          const errorBody = body as Record<string, unknown>;
          if (errorBody.error === 'STREAM_NOT_FOUND') {
            return new StreamNotFoundError(
              'Stream not found',
              body,
              'unknown',
              'unknown'
            );
          }
        }
        return new EventStoreNotFoundError(
          'Resource not found',
          body,
          'unknown'
        );

      case 408:
        return new TimeoutError('Request timeout', body, 0, 'unknown');

      case 409:
        return new VersionConflictError('Conflict detected', body, -1, -1);

      case 429: {
        const retryAfter =
          typeof body === 'object' && body !== null
            ? ((body as Record<string, unknown>).retryAfter as
                | number
                | undefined)
            : undefined;
        return new RateLimitError('Rate limit exceeded', body, retryAfter);
      }

      default:
        return new InternalServerError(`HTTP Error: ${status}`, body);
    }
  }

  /**
   * Type guard for version conflict error responses
   */
  private isVersionConflictError(
    body: Record<string, unknown>
  ): body is VersionConflictErrorResponse {
    return (
      body.error === 'Version conflict' &&
      typeof body.details === 'string' &&
      typeof body.currentVersion === 'number' &&
      typeof body.expectedVersion === 'number'
    );
  }

  /**
   * Type guard for validation error responses
   */
  private isValidationError(
    body: Record<string, unknown>
  ): body is ValidationErrorResponse {
    return (
      body.error === 'Invalid request body' &&
      Array.isArray(body.details) &&
      body.details.every(
        (detail: unknown) =>
          typeof detail === 'object' &&
          detail !== null &&
          typeof (detail as Record<string, unknown>).code === 'string' &&
          typeof (detail as Record<string, unknown>).message === 'string' &&
          Array.isArray((detail as Record<string, unknown>).path)
      )
    );
  }

  /**
   * Type guard for general error responses
   */
  private isGeneralError(
    body: Record<string, unknown>
  ): body is GeneralErrorResponse {
    return typeof body.error === 'string' && typeof body.message === 'string';
  }
}
